import { useEffect, useRef } from "react";
import type { HoverStore } from "./hoverStore";

/**
 * Spotlight hold — the "point faster" gesture. While the hold key (**⌥ Option**)
 * is down, the camera live-tracks whatever the caret or the pointer touches;
 * releasing glides back to where the hold began.
 *
 * Design points (see the camera-operator v2 plan):
 *  - `e.code`-matched (AltLeft/AltRight) + capture phase, so it fires with the
 *    editor focused (macOS Option-composition never reaches `e.key`).
 *  - Registered whenever `active` (the operator surface is interactive and not a
 *    forced demo) — it's the always-available "show me" gesture, so it works
 *    whether or not smart-view autopilot is toggled on (read-only embeds and the
 *    forced marketing demo never bind it).
 *  - **Add Shift mid-hold (⌥⇧)** to widen from the pointed node to the OVERVIEW
 *    of its containing space — the "where am I?" glance; release Shift to return
 *    to the node, release ⌥ to glide home.
 *  - Hover wins whenever a hovered id is present; the caret is the fallback.
 *  - Hover-driven retargets are gated on a real `pointermove` since the last
 *    frame — framing moves the board under a stationary pointer, which would
 *    otherwise change the hovered id and feed an oscillation.
 *  - A user board gesture during the hold latches it dead until keyup (`onGesture`,
 *    wired alongside the operator's `noteUserNavigation`); `blur` /
 *    `visibilitychange` force-end a hold whose keyup was missed (cmd-tab).
 */
export interface SpotlightHoldOpts {
  readonly active: boolean;
  /** Board ids the caret currently sits inside (innermost first). */
  readonly caretIds: readonly string[];
  readonly hoverStore: HoverStore;
  readonly start: () => void;
  readonly target: (ids: readonly string[]) => void;
  /** Overview glance (⌥⇧): frame the space CONTAINING `anchorId` (or the root
   *  when null). Same live hold — Shift just widens the target. */
  readonly overview: (anchorId: string | null) => void;
  readonly end: () => void;
}

export interface SpotlightHoldHandle {
  /** Latches the active hold dead until keyup — call on a real board gesture. */
  readonly onGesture: () => void;
}

const HOLD_CODES = new Set(["AltLeft", "AltRight"]);
const SHIFT_CODES = new Set(["ShiftLeft", "ShiftRight"]);

export function useSpotlightHold(opts: SpotlightHoldOpts): SpotlightHoldHandle {
  // Mirror the live opts through a ref so the window listeners register once
  // (keyed on `active`) yet always call the latest callbacks / read fresh state.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const heldRef = useRef(false);
  const shiftRef = useRef(false); // Shift widens the target to the overview
  const deadRef = useRef(false); // gesture latch: dropped targets until keyup
  const lastPointerMoveAtRef = useRef(0);
  const lastFrameAtRef = useRef(0);
  // Entry point the caret effect calls to retarget the live hold; the main
  // effect wires it (and clears it on teardown).
  const caretRetargetRef = useRef<(() => void) | null>(null);

  const handleRef = useRef<SpotlightHoldHandle>({
    onGesture: () => {
      if (heldRef.current) deadRef.current = true;
    },
  });

  useEffect(() => {
    if (!opts.active) return;

    const bestAnchor = (): string | null => {
      const hovered = optsRef.current.hoverStore.getHoveredId();
      if (hovered) return hovered;
      const caret = optsRef.current.caretIds;
      return caret.length > 0 ? caret[0]! : null;
    };
    const applyTarget = (): void => {
      const anchor = bestAnchor();
      // Shift widens to the overview of the anchor's space (null ⇒ root fit);
      // otherwise frame the node itself. Both feed the same live hold.
      if (shiftRef.current) {
        optsRef.current.overview(anchor);
        lastFrameAtRef.current = performance.now();
        return;
      }
      if (anchor === null) return;
      optsRef.current.target([anchor]);
      lastFrameAtRef.current = performance.now();
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (SHIFT_CODES.has(e.code)) {
        // Shift pressed mid-hold → widen to the overview.
        if (heldRef.current && !deadRef.current && !shiftRef.current) {
          shiftRef.current = true;
          applyTarget();
        }
        return;
      }
      if (!HOLD_CODES.has(e.code) || e.repeat || heldRef.current) return;
      heldRef.current = true;
      deadRef.current = false;
      shiftRef.current = e.shiftKey; // Shift already down ⇒ start on the overview
      // Reset the pointer-move gate so the first hover after keydown needs a
      // fresh move; the keydown itself targets directly (not gated).
      lastPointerMoveAtRef.current = 0;
      lastFrameAtRef.current = 0;
      optsRef.current.start();
      applyTarget();
    };
    const endHold = (): void => {
      if (!heldRef.current) return;
      heldRef.current = false;
      deadRef.current = false;
      shiftRef.current = false;
      optsRef.current.end();
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (HOLD_CODES.has(e.code)) {
        endHold();
        return;
      }
      if (SHIFT_CODES.has(e.code) && heldRef.current && !deadRef.current && shiftRef.current) {
        // Shift released while still holding ⌥ → back to the pointed node.
        shiftRef.current = false;
        applyTarget();
      }
    };
    const onPointerMove = (): void => {
      lastPointerMoveAtRef.current = performance.now();
      // A real pointer move that changed the hovered node retargets; the hover
      // subscription below handles the actual id read.
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("keyup", onKeyUp, { capture: true });
    window.addEventListener("blur", endHold);
    document.addEventListener("visibilitychange", endHold);
    window.addEventListener("pointermove", onPointerMove, { passive: true });

    // Hover changes retarget — but only when a real pointer move produced them
    // (guards the board-moves-under-a-stationary-pointer feedback loop).
    const unsubHover = optsRef.current.hoverStore.subscribe(() => {
      if (!heldRef.current || deadRef.current) return;
      if (lastPointerMoveAtRef.current <= lastFrameAtRef.current) return;
      applyTarget();
    });

    // Store the caret-retarget entry point where the caret effect can reach it.
    caretRetargetRef.current = () => {
      if (!heldRef.current || deadRef.current) return;
      applyTarget();
    };

    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.removeEventListener("keyup", onKeyUp, { capture: true });
      window.removeEventListener("blur", endHold);
      document.removeEventListener("visibilitychange", endHold);
      window.removeEventListener("pointermove", onPointerMove);
      unsubHover();
      caretRetargetRef.current = null;
      // Leaving the active state mid-hold: finalize so no stale hold lingers.
      endHold();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.active]);

  // Caret moved (prop changed) → retarget the live hold. Always honored (a caret
  // move is genuine intent, unlike a phantom hover under a moving board).
  const caretKey = opts.caretIds.join(" ");
  useEffect(() => {
    caretRetargetRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caretKey]);

  return handleRef.current;
}
